import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { xhrRequest } from '../lib/xhrRequest';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Camera, Loader2, User, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';

export default function OnboardingProfilePage() {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    displayName: user?.displayName || '',
    serviceTitle: user?.serviceTitle || ''
  });
  const [photoPreview, setPhotoPreview] = useState(user?.photoUrl || null);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (500KB)
    if (file.size > 500 * 1024) {
      toast.error('Photo must be less than 500KB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Note: do NOT set Content-Type — the browser supplies the multipart
      // boundary automatically when sending FormData via XHR.
      const { ok, status, data } = await xhrRequest('POST', `${API}/users/upload-photo`, {
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!ok) {
        throw new Error(data.detail || `Failed to upload photo (${status})`);
      }

      setPhotoPreview(data.photoUrl);
      updateUser({ photoUrl: data.photoUrl });
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.displayName || !formData.serviceTitle) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const { ok, status, data: updatedUser } = await xhrRequest(
        'PUT',
        `${API}/users/profile`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!ok) {
        throw new Error(updatedUser.detail || `Failed to update profile (${status})`);
      }

      updateUser(updatedUser);
      toast.success('Profile updated!');
      navigate('/onboarding/payouts');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#050505]">
      <div className="auth-container flex-1">
      <div className="auth-card animate-fade-in max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-full bg-[#F5C542] text-black flex items-center justify-center font-bold text-sm">1</span>
            <div className="w-16 h-0.5 bg-white/20" />
            <span className="w-8 h-8 rounded-full bg-white/10 text-white/50 flex items-center justify-center font-bold text-sm">2</span>
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2">Set Up Your Profile</h1>
          <p className="text-[#A1A1AA]">This is what guests will see when they tip you</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo Upload */}
          <div className="flex flex-col items-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
              data-testid="photo-upload-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="relative w-24 h-24 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden hover:border-[#F5C542] transition-colors group"
              data-testid="photo-upload-button"
            >
              {photoPreview ? (
                <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User size={32} className="text-[#52525B]" />
              )}
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingPhoto ? (
                  <Loader2 className="animate-spin text-white" size={24} />
                ) : (
                  <Camera size={24} className="text-white" />
                )}
              </div>
            </button>
            <p className="text-xs text-[#52525B] mt-2">Add a photo (optional)</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-xs uppercase tracking-widest text-[#A1A1AA]">Display Name</Label>
            <Input
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Tasha"
              className="bg-white/5 border-white/10 focus:border-[#F5C542] h-12 text-white placeholder:text-[#52525B]"
              data-testid="profile-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="serviceTitle" className="text-xs uppercase tracking-widest text-[#A1A1AA]">Service Title</Label>
            <Input
              id="serviceTitle"
              name="serviceTitle"
              value={formData.serviceTitle}
              onChange={handleChange}
              placeholder="Mobile Bartender"
              className="bg-white/5 border-white/10 focus:border-[#F5C542] h-12 text-white placeholder:text-[#52525B]"
              data-testid="profile-service-input"
            />
          </div>

          {/* Preview */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="text-xs uppercase tracking-widest text-[#52525B] mb-3">Preview</p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden">
                {photoPreview ? (
                  <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-lg font-bold">
                    {formData.displayName ? formData.displayName[0].toUpperCase() : '?'}
                  </span>
                )}
              </div>
              <div>
                <p className="font-semibold">{formData.displayName || 'Your Name'}</p>
                <p className="text-sm text-[#A1A1AA]">{formData.serviceTitle || 'Your Service'}</p>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center justify-center gap-2"
            data-testid="profile-continue-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Saving...
              </>
            ) : (
              <>
                Continue
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>
      </div>
      </div>
      <Footer />
    </div>
  );
}
